/**
 * 开通弹窗内容渲染器注册表（通用，对具体业务零认知）
 *
 * store 只放可序列化的 { variant, data }；业务功能用 registerPaymentContent
 * 注册 (data) => 插槽JSX；弹窗用 resolvePaymentContent 按 variant 查表渲染。
 * 查不到 / 渲染器抛错 → 返回空插槽，由弹窗兜底现状文案。
 */
import type { ReactNode } from 'react'

export type PaymentTabKey = 'basic' | 'advanced' | 'super' | 'enterprise'

/** 渲染器产出的"插槽内容" · 缺哪个弹窗兜底哪个 */
export interface PaymentContentSlots {
  title?: ReactNode
  subtitle?: ReactNode
  /** 业务内容按会员档位为支付弹窗头部提供的视觉主题类名。 */
  headerClassNames?: Partial<Record<PaymentTabKey, string>>
  tabNotes?: Partial<Record<PaymentTabKey, ReactNode>>
  /** 锁定会员档位；设置后默认打开该页，其他会员页不可切换 */
  lockedTabKey?: PaymentTabKey
  /** 最低可选会员档位；默认打开该页，比它低的档位不可切换，更高档位仍可跳选 */
  minimumTabKey?: PaymentTabKey
  /** 各会员 tab 商品卡片上方一行自定义横幅（per-tab，缺失不渲染） */
  cardBanner?: Partial<Record<PaymentTabKey, ReactNode>>
}

/** variant 的渲染器 · 入参是 store 里可序列化 data，出参是 JSX 插槽 */
export type PaymentContentRenderer = (data: any) => PaymentContentSlots | null

const registry = new Map<string, PaymentContentRenderer>()

/** 业务功能注册自己的渲染器（模块加载时调用一次） */
export function registerPaymentContent(
  variant: string,
  renderer: PaymentContentRenderer
): void {
  registry.set(variant, renderer)
}

/** 弹窗唯一消费入口：{variant,data} → 插槽；查不到/抛错都回空插槽（→ 兜底） */
export function resolvePaymentContent(
  variant?: string,
  data?: any
): PaymentContentSlots {
  if (!variant) return {}
  const renderer = registry.get(variant)
  if (!renderer) return {}
  try {
    return renderer(data) || {}
  } catch {
    return {}
  }
}
